<template>
  <LmDialog v-model="open" title="Subscription Claim">
    <template #toolbar-actions>
      <q-btn color="tertiary" size="sm" @click="prepareSend">
        Send
      </q-btn>
    </template>

    <template #content>
      <q-card-section :style="{ height: '600px', overflow: 'auto' }">
        <div class="row">
          <SpinnerAudioLoader :loading="!claims" />
          <template v-if="claims">
            <ag-grid-wrapper
              ref="contactsWrapper"
              :options="contactsOptions"
              :preselect-rows="true"
              :flat-card="true"
              title="Email will be sent to:"
              title-inset
              ag-grid-class="no-horizontal-margin ag-theme-material"
              store-namespace="data"
              service-namespace="contacts"
              needs-update-preprocessing
              @selectedRows="selectContacts"
              @requested-update="prepareSubmission"
            >
              <template #footerActions>
                <div class="actions">
                  <LinkButton text="Create publisher contact" @click="createContact('publisher')" />
                  <LinkButton v-if="period.vendor" text="Create vendor contact" @click="createContact('vendor')" />
                </div>
              </template>
            </ag-grid-wrapper>
            <TemplateAndAttachments
              ref="templateAndAttachments"
              :template.sync="claims.email_template"
              @attachments="updateAttachments"
            />
          </template>
        </div>
      </q-card-section>

      <ContactModal
        v-model="showForm"
        :main-address="mainAddress"
        :provider="{ id: entityId }"
        @created="refreshContacts"
      />
    </template>
  </LmDialog>
</template>

<script>
import * as apiTo from '@api/api-routes'
import { getDefaultAddress } from '@/reusable/address-fields/default-address'
import { LmFormObject } from '@/lib/models/lm-form-object.class'
import { personNameSorter, personRenderer } from '@/lib/generic.utils'
// Libraries
import cloneDeep from 'lodash/cloneDeep'
// Components
import SpinnerAudioLoader from '@/reusable/base-components/SpinnerAudioLoader'
import LinkButton from '@/reusable/base-components/LinkButton'
import AgGridWrapper from '@components/ag-grid-helpers/AgGridWrapper'
import LmDialog from '@/reusable/base-components/LmDialog/LmDialog'
import ContactModal from '@/reusable/contact-modal/ContactModal'
import TemplateAndAttachments from './TemplateAndAttachments'

export default {
  components: {
    SpinnerAudioLoader,
    LinkButton,
    LmDialog,
    AgGridWrapper,
    ContactModal,
    TemplateAndAttachments
  },

  props: {
    subscription: Object,
    period: Object
  },

  data() {
    return {
      entityId: null,
      open: false,
      claims: null,
      showForm: false,
      selectedContacts: [],
      attachments: [],
      loading: false,
      mainAddress: getDefaultAddress('main')
    }
  },

  computed: {
    contactsOptions() {
      return {
        rowSelection: 'multiple',
        suppressRowClickSelection: true,
        rowData: this.claims.contacts,
        columnDefs: [
          {
            headerName: 'Name',
            checkboxSelection: true,
            headerCheckboxSelection: true,
            comparator: personNameSorter,
            cellRenderer: params => personRenderer(params.data),
            tooltipValueGetter: params => personRenderer(params.data)
          },
          {
            headerName: 'Job Title',
            field: 'job',
            tooltipField: 'job'
          },
          {
            headerName: 'Publisher/Vendor',
            cellRenderer: params => params.data.provider.name
          },
          {
            headerName: 'Phone',
            field: 'phone',
            tooltipField: 'phone'
          },
          {
            headerName: 'Email',
            field: 'email',
            tooltipField: 'email'
          }
        ]
      }
    }
  },

  watch: {
    'period.vendor': {
      handler: 'getSubscriptionClaims',
      deep: true,
      immediate: true
    }
  },

  created() {
    this.entityId = this.subscription.publisher.id
    this.copiedClaims = null
  },

  methods: {
    openDialog() {
      this.open = true
    },
    getSubscriptionClaims() {
      apiTo.getSubscriptionClaims(this.period.id).then(({ data }) => {
        this.claims = data.result
        this.copiedClaims = cloneDeep(data.result)
      })
    },
    refreshContacts(newContact) {
      newContact.preselected = true
      this.claims.contacts.push(newContact)
      // update cuz we need contacts when we refresh component's data
      this.copiedClaims = cloneDeep(this.claims)
      this.$refs.contactsWrapper.updateSelectedRows()
    },
    updateAttachments(attachments) {
      this.attachments = attachments
    },
    selectContacts(event) {
      this.selectedContacts = event
    },
    prepareSubmission(event) {
      let payload = cloneDeep(event.item)
      payload.email_preferences = event.item.email_preferences.map(item => item.id)
      this.$refs.contactsWrapper.$emit('update', payload)
    },
    prepareSend() {
      const payload = {
        entity_id: this.claims.entity_id,
        contacts: this.selectedContacts,
        email_template: this.claims.email_template,
        attachments: this.attachments.map(att => att.file)
      }
      let formDataSource = new LmFormObject(payload)
      delete formDataSource.errors
      delete formDataSource.originalData
      let formData = new FormData()
      formDataSource.getFormData(formData, payload)
      payload.attachments.forEach((file, index) => formData.append(`attachments[${index}]`, file))
      this.send(formData)
    },
    send(payload) {
      this.loading = true
      apiTo
        .sendEmail(payload)
        .then(() => {
          this.open = false
          this.resetComponentsData()
        })
        .finally(() => (this.loading = false))
    },
    createContact(type) {
      this.entityId = this.period[type].id
      this.showForm = true
    },
    resetComponentsData() {
      Object.assign(this.$data, this.$options.data.apply(this))
      this.$refs.templateAndAttachments.resetEmailAttachments()
      this.claims = cloneDeep(this.copiedClaims)
      this.entityId = this.subscription.publisher.id
    }
  }
}
</script>

<style lang="stylus" scoped>
@import '~@/styles/stylus/mixins'

tableWithCheckBoxes()

.q-card {
  width: 1200px;
  max-width: 1200px;
}

.actions {
  display: grid;
  grid-auto-flow: column;
  grid-gap: 10px;
  padding: 18px 18px 0;
}

>>> .table-content {
  padding 0 20px
}
</style>
